import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components, StylesConfig } from 'react-select';
import FormItem from "app/components/inputs/FormItem";
import styles from '../../routes/Calendar/EventForm/styles.module.scss';

const customStyles: StylesConfig = {
  control: (base: any, state: any) => ({
    ...base,
    border: '1px solid #E7E7E7',
    borderRadius: 12,
    paddingLeft: 4,
    borderColor: state.isFocused ? '#039be5' : '#E7E7E7',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(3, 155, 229, 0.15)' : 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: state.isFocused ? '#039be5' : '#E7E7E7',
    },
  }),
  menu: (base: any) => ({
    ...base,
    animation: 'fadeInMenu 0.15s ease',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    color: 'black',
    fontWeight: '400',
    backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
    cursor: 'pointer',
  }),
  multiValue: (styles: any) => ({
    ...styles,
    backgroundColor: '#27a4ff',
    padding: '4px',
    borderRadius: '4px',
  }),
  multiValueLabel: (styles: any) => ({
    ...styles,
    color: 'white',
    fontSize: '16px',
  }),
  multiValueRemove: (styles: any) => ({
    ...styles,
    color: 'white',
    ':hover': {
      color: 'white',
    },
  }),
  singleValue: (provided: any, state: any) => ({
    ...provided,
    ...(!state.isDisabled && { color: 'black' }),
  }),
  container: (provided: any) => ({
    ...provided,
    color: '#8C8C8C',
  }),
  placeholder: (defaultStyles: any) => ({
    ...defaultStyles,
    color: '#8c8c8c',
  }),
};

const durationCustomStyles: StylesConfig = {
  ...customStyles,
  valueContainer: (base: any) => ({
    ...base,
    height: '46px',
    display: 'flex',
    alignItems: 'center',
  }),
  input: () => ({
    height: '42px',
  }),
  menuList: () => ({
    cursor: 'pointer'
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
};

const DropdownIndicator = (props: any) => {
  const open = props.selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        aria-hidden="true"
      >
        <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </components.DropdownIndicator>
  );
};

export { customStyles, durationCustomStyles, DropdownIndicator };

type Props = {
  t: (key: string) => string;
  control: any;
  watchPaymentMethod: string;
  selectedAthletes: any[];
  allowsPayments: boolean;
  defaultPaymentMethod?: string;
  isDisabled?: boolean;
};

export default function PaymentSelector({
  t,
  control,
  watchPaymentMethod,
  selectedAthletes,
  allowsPayments,
  defaultPaymentMethod = 'offline',
  isDisabled = false,
}: Props) {
  const { setValue, getValues, clearErrors } = useFormContext();

  const paymentOptions = useMemo(() => {
    const options = [];
    if (allowsPayments) {
      options.push({ value: "online", label: t("Online") });
    }
    options.push({ value: "offline", label: t("Offline") });
    if (selectedAthletes.length === 1) {
      options.push({ value: "package", label: t("Package / Subscription") });
    }
    return options;
  }, [allowsPayments, selectedAthletes.length, t]);

  useEffect(() => {
    if (selectedAthletes.length === 1) {
      setValue('paying_athlete_id', selectedAthletes[0].id, { shouldValidate: true, shouldDirty: true });
    } else if (selectedAthletes.length > 1) {
      const currentValue = getValues('paying_athlete_id');
      const isStillValid = currentValue && selectedAthletes.some(a => a.id === currentValue);
      if (!isStillValid) {
        setValue('paying_athlete_id', undefined, { shouldValidate: true, shouldDirty: true });
      }
    } else {
      setValue('paying_athlete_id', undefined, { shouldValidate: true, shouldDirty: true });
    }
  }, [watchPaymentMethod, selectedAthletes, setValue]);

  useEffect(() => {
    const exactlyOne = selectedAthletes.length === 1;

    if (!exactlyOne && watchPaymentMethod === 'package') {
      setValue('payment_method', defaultPaymentMethod, { shouldValidate: true });
      setValue('selected_package_id', undefined);
      clearErrors('selected_package_id');
    }
  }, [selectedAthletes.length, watchPaymentMethod, setValue, selectedAthletes, defaultPaymentMethod]);

  const paymentMethodSelect = (
    <Controller
      name="payment_method"
      control={control}
      rules={{ required: t("This field is required") }}
      render={({ field, fieldState }) => (
        <>
          <Select
            {...field}
            value={paymentOptions.find(opt => opt.value === field.value) ?? null}
            onChange={(option) => field.onChange((option as { value: string }).value)}
            options={paymentOptions}
            styles={durationCustomStyles}
            placeholder={t("Select Payment Method")}
            components={{ IndicatorSeparator: () => null, DropdownIndicator }}
            isSearchable={false}
            isDisabled={isDisabled}
            classNamePrefix="rs"
            menuPortalTarget={document.body}
            menuPlacement="auto"
          />
          {fieldState.error && <span className={styles.error_payment}>{fieldState.error.message}</span>}
        </>
      )}
    />
  );

  return (
    <FormItem className={styles.input_field}>
      <label className={styles.sr_only}>{t("Payment Method")}</label>
      {paymentMethodSelect}
    </FormItem>
  );
}
